import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { ArrowLeft } from "lucide-react";

const TABS = ["Overview", "Users", "Entitlements", "Activity"];

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [tenant, setTenant] = useState(null);
  const [sub, setSub] = useState(null);
  const [users, setUsers] = useState([]);
  const [ent, setEnt] = useState({});
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get(`/v1/tenants/${id}`).then((r) => setTenant(r.data.data)).catch(() => {});
    api.get(`/v1/tenants/${id}/subscription`).then((r) => setSub(r.data.data)).catch(() => {});
    api.get(`/v1/tenants/${id}/users`).then((r) => setUsers(r.data.data)).catch(() => {});
    api.get(`/v1/tenants/${id}/entitlements`).then((r) => setEnt(r.data.data)).catch(() => {});
    api.get(`/v1/tenants/${id}/activity`).then((r) => setActivity(r.data.data)).catch(() => {});
  }, [id]);

  if (!tenant) return <div className="text-slate-400">Loading…</div>;

  return (
    <div data-testid="tenant-detail-page">
      <button onClick={() => navigate("/admin/tenants")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="back-to-tenants">
        <ArrowLeft className="h-4 w-4" /> Tenants
      </button>
      <PageHeader
        title={tenant.tenantName}
        subtitle={`${tenant.tenantCode} · ${tenant.industry || "—"} · ${tenant.deploymentMode}`}
        actions={<Badge color={tenant.status === "Active" ? "green" : "amber"}>{tenant.status}</Badge>}
      />

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Info title="Tenant" items={[
            ["Code", tenant.tenantCode], ["Name", tenant.tenantName], ["Industry", tenant.industry || "—"],
            ["Region", tenant.region || "—"], ["Deployment", tenant.deploymentMode], ["Status", tenant.status],
          ]} />
          <Info title="Subscription" items={sub ? [
            ["Plan", sub.planCode], ["Status", sub.status], ["Billing", sub.billingCycle],
            ["Industry Packs", (sub.industryPackCodes || []).join(", ") || "None"],
            ["Auto Renew", String(sub.autoRenew)],
          ] : [["—", "No subscription"]]} />
        </div>
      )}

      {tab === "Users" && (
        <DataTable
          testid="tenant-users-table"
          columns={[
            { key: "email", header: "Email" },
            { key: "name", header: "Name", render: (u) => `${u.firstName} ${u.lastName}` },
            { key: "roleCodes", header: "Roles", render: (u) => (u.roleCodes || []).join(", ") },
            { key: "status", header: "Status", render: (u) => <Badge color={u.status === "Active" ? "green" : "amber"}>{u.status}</Badge> },
          ]}
          rows={users} loading={false}
        />
      )}

      {tab === "Entitlements" && (
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(ent).map(([type, codes]) => (
            <div key={type} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-3">{type}s <span className="text-slate-400 font-normal">({codes.length})</span></h3>
              <div className="flex flex-wrap gap-1.5">
                {codes.map((c) => <Badge key={c} color="teal">{c}</Badge>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Activity" && (
        <DataTable
          testid="tenant-activity-table"
          columns={[
            { key: "timestamp", header: "Time", render: (a) => (a.timestamp || "").slice(0, 19).replace("T", " ") },
            { key: "entityType", header: "Entity", render: (a) => <Badge>{a.entityType}</Badge> },
            { key: "action", header: "Action" },
            { key: "actorEmail", header: "Actor", render: (a) => a.actorEmail || "—" },
          ]}
          rows={activity} loading={false}
        />
      )}
    </div>
  );
}

function Info({ title, items }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <h3 className="font-semibold text-slate-800 mb-4">{title}</h3>
      <dl className="space-y-2.5">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <dt className="text-slate-500">{k}</dt>
            <dd className="font-medium text-slate-800 text-right">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
